import type {
  CrmCampaignChannel,
  CrmDeliveryStatus,
  CrmGender,
  CrmLeadSource,
  CrmLeadStatus,
  CrmSkipReason,
} from '@behuman/core-contract'
import type { LeadMilestone } from './hooks'

export const LEAD_STATUS_LABELS: Record<CrmLeadStatus, string> = {
  new: 'New',
  confirmed: 'Verified Mobile',
  registered: 'Registered',
  scheduled: 'Scheduled',
  canceled: 'Canceled appointment',
  completed: 'Completed appointment',
  'no-show': 'No-show',
  'not-interested': 'Not interested',
}

/** A chip's tint and lettering: the Patient list's soft status chips (Invited, Active). */
export const CHIP_TONES = {
  gray: { bg: '#EEF1F5', fg: '#4A5568' },
  blue: { bg: '#E8F0FB', fg: '#2A5F9E' },
  green: { bg: '#E6F4EA', fg: '#1E6B3A' },
  teal: { bg: '#E3F3F1', fg: '#1C6B62' },
  orange: { bg: '#FDF0E3', fg: '#A35208' },
  amber: { bg: '#FDF6E3', fg: '#8A5D00' },
  red: { bg: '#FDECEA', fg: '#B42318' },
} as const

export type ChipColor = keyof typeof CHIP_TONES

export const LEAD_STATUS_COLORS: Record<CrmLeadStatus, ChipColor> = {
  new: 'gray',
  confirmed: 'gray',
  registered: 'blue',
  scheduled: 'green',
  canceled: 'orange',
  completed: 'teal',
  'no-show': 'amber',
  // The opt-out: campaigns skip it and appointments never move it.
  'not-interested': 'red',
}

export const LEAD_SOURCE_LABELS: Record<CrmLeadSource, string> = {
  'google-ads': 'Google Ads',
  'facebook-ads': 'Facebook Ads',
  website: 'Website / Organic',
  zocdoc: 'Zocdoc',
  manual: 'Manual / Import',
  unknown: 'Unknown',
}

export const GENDER_LABELS: Record<CrmGender, string> = {
  female: 'Female',
  male: 'Male',
  other: 'Other',
  unknown: 'Unknown',
}

export const LEAD_MILESTONE_LABELS: Record<LeadMilestone, string> = {
  registered: 'Registered',
  booked: 'Booked or seen',
}

export const CHANNEL_LABELS: Record<CrmCampaignChannel, string> = {
  sms: 'SMS',
  email: 'Email',
  both: 'SMS + email',
}

export const DELIVERY_STATUS_LABELS: Record<CrmDeliveryStatus, string> = {
  pending: 'Waiting to send',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
}

export const DELIVERY_STATUS_COLORS: Record<CrmDeliveryStatus, ChipColor> = {
  pending: 'gray',
  sending: 'blue',
  sent: 'green',
  failed: 'red',
  skipped: 'amber',
}

export const SKIP_REASON_LABELS: Record<CrmSkipReason, string> = {
  'not-interested': 'Not interested',
  'no-email': 'No email address',
  'no-phone': 'No textable phone',
}

/** The tracking fields in the order the details show them. */
export const ATTRIBUTION_FIELD_LABELS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmTerm: 'utm_term',
  gclid: 'Google click id',
  fbclid: 'Facebook click id',
  landingPage: 'Landing page',
  referrer: 'Referrer',
} as const

/** A failed request's own message, else the fallback. */
export const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback
